import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Slack, Github, Building2, Mail, HardDrive, RefreshCw, Unplug, CheckCircle2,
  Loader2, Video, FileText, Cloud, Table,
} from "lucide-react";
import { toast } from "sonner";

const CARDS = [
  { id: "slack", name: "Slack", icon: Slack, desc: "Channel messages become decisions and activity entries.", ready: true },
  { id: "gmail", name: "Gmail", icon: Mail, desc: "Sent-email summaries — bodies never stored raw.", ready: true },
  { id: "github", name: "GitHub", icon: Github, desc: "Commit messages and PR titles across your repos.", ready: true },
  { id: "gdrive", name: "Google Drive", icon: HardDrive, desc: "File activity metadata — names and folders only.", ready: true },
  { id: "hubspot", name: "HubSpot", icon: Building2, desc: "Notes, calls, and email activities you logged.", ready: true },
  { id: "teams", name: "Microsoft Teams", icon: Video, desc: "Coming soon", ready: false },
  { id: "zoom", name: "Zoom", icon: Video, desc: "Coming soon", ready: false },
  { id: "notion", name: "Notion", icon: FileText, desc: "Coming soon", ready: false },
  { id: "salesforce", name: "Salesforce", icon: Cloud, desc: "Coming soon", ready: false },
];

export default function Integrations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/integrations/status");
      setRows(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load integrations");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const connect = async (platform) => {
    try {
      const { data } = await api.post(`/integrations/${platform}/authorize`);
      if (data.mocked) {
        toast.success(`${platform} connected (mock mode — add ${platform.toUpperCase()}_CLIENT_ID for live OAuth)`);
        load();
      } else if (data.authorize_url) {
        window.location.href = data.authorize_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to connect");
    }
  };

  const sync = async (platform) => {
    setSyncing(platform);
    try {
      const { data } = await api.post(`/integrations/${platform}/sync`);
      toast.success(`Synced ${data.entries_captured} entries from ${platform} (${data.mode} mode)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally { setSyncing(null); }
  };

  const disconnect = async (platform) => {
    const purge = window.confirm(`Disconnect ${platform}?\n\nOK = disconnect AND delete captured entries\nCancel = disconnect but keep entries`);
    setDisconnecting(platform);
    try {
      const { data } = await api.delete(`/integrations/${platform}/disconnect`, { params: { delete_entries: purge } });
      toast.success(purge ? `Disconnected, ${data.purged_entries} entries removed` : "Disconnected — entries kept");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to disconnect");
    } finally { setDisconnecting(null); }
  };

  const stateOf = (id) => rows.find((r) => r.platform === id) || {};

  return (
    <div className="p-8 max-w-5xl" data-testid="integrations-page">
      <header className="mb-8">
        <p className="eyebrow mb-3">Integrations</p>
        <h1 className="font-display text-3xl">Platform capture</h1>
        <p className="mt-2 text-muted-foreground">
          Connect the tools you work in — RAGNES captures your activity into Individual Memory, filtered by your consent settings and the privacy hard-blocks.
        </p>
      </header>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CARDS.map((c) => {
            const s = stateOf(c.id);
            const Icon = c.icon;
            return (
              <div key={c.id} className={`bento p-5 flex flex-col gap-3 ${!c.ready ? "opacity-50" : ""}`} data-testid={`int-card-${c.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-md bg-secondary/60 flex items-center justify-center">
                    <Icon className="w-4.5 h-4.5 w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm flex items-center gap-2">
                      {c.name}
                      {s.connected && <span className="w-2 h-2 rounded-full bg-emerald-500" data-testid={`int-dot-${c.id}`} />}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {c.ready ? (s.connected ? (s.mocked ? "connected · mock" : "connected") : "not connected") : "coming soon"}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed flex-1">{c.desc}</p>

                {c.ready && s.connected && (
                  <p className="text-[10px] font-mono text-muted-foreground">
                    {s.last_sync ? `last sync ${new Date(s.last_sync).toLocaleString()}` : "never synced"}
                    {" · "}{s.entries_captured ?? 0} captured
                  </p>
                )}

                {c.ready && (
                  <div className="flex gap-2">
                    {!s.connected ? (
                      <Button size="sm" onClick={() => connect(c.id)} className="rounded-full" data-testid={`int-connect-${c.id}`}>
                        Connect
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => sync(c.id)} disabled={syncing === c.id} className="rounded-full" data-testid={`int-sync-${c.id}`}>
                          {syncing === c.id
                            ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Syncing…</>
                            : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Re-sync</>}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => disconnect(c.id)} disabled={disconnecting === c.id} className="rounded-full text-muted-foreground hover:text-destructive" data-testid={`int-disconnect-${c.id}`}>
                          <Unplug className="w-3.5 h-3.5 mr-1.5" /> Disconnect
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="card-flat mt-8 p-5 border-emerald-500/30 bg-emerald-500/5">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="text-emerald-500 font-medium">Privacy rules:</span> every captured item passes the hard-block filter
            (passwords, cards, bank details, IDs, WhatsApp, keys, tokens). Gmail bodies are summarised before storage,
            Slack DMs are never captured, GitHub stores file names only, and HubSpot notes over 200 characters are summarised.
          </p>
        </div>
      </div>
    </div>
  );
}
